import { useMemo, useEffect, useState, useCallback } from 'react';

import {
    getCountries,
    getStatesForCountry,
    ensureGeoDataLoaded,
    type SupportedLanguage,
    AVAILABLE_LANGUAGES,
    importLanguage,
    isLanguageRegistered,
    getRegisteredLanguages
} from '../../../data/iso3166';

export interface ISOCountry {
    iso: string;
    iso3: string;
    numeric: number;
    name: string;
}

export interface ISORegion {
    iso: string;
    name: string;
}

export interface UseISODataResult {
    countries: Record<string, ISOCountry>;
    getRegions: (countryCode: string) => ISORegion[];
    findRegion: (countryCode: string, regionCode: string) => ISORegion | null;
    mapGoogleToISO: (googleState: string, googleStateLong: string, countryCode: string) => ISORegion | null;
    isLanguageLoaded: boolean;
    registeredLanguages: SupportedLanguage[];
}

/**
 * React hook for accessing ISO3166 countries and regions data.
 * Fetches slim JSON from CDN on first use, then caches in memory.
 */
export function useISOData(
    language: SupportedLanguage = 'en',
    autoImport = true,
    disputeSetting = 'UN'
): UseISODataResult {
    // Normalize: if the caller passed an unsupported locale (e.g. 'nl'),
    // silently fall back to English so the country dropdown still renders.
    // The CDN only hosts languages listed in AVAILABLE_LANGUAGES; any other
    // value would 404 and leave `isLanguageLoaded` stuck on false.
    const effectiveLanguage: SupportedLanguage =
        (AVAILABLE_LANGUAGES as readonly string[]).includes(language) ? language : 'en';

    const [isLanguageLoaded, setIsLanguageLoaded] = useState(isLanguageRegistered(effectiveLanguage));
    const [registeredLanguages, setRegisteredLanguages] = useState<SupportedLanguage[]>(getRegisteredLanguages);

    // Fetch geodata from CDN (countries + regions) for the requested language
    useEffect(() => {
        let cancelled = false;

        ensureGeoDataLoaded(effectiveLanguage)
            .then(() => {
                if (!cancelled) {
                    setIsLanguageLoaded(true);
                    setRegisteredLanguages(getRegisteredLanguages());
                }
            })
            .catch((err) => {
                console.error('[SDK] Failed to load geodata from CDN:', err);
                if (cancelled) return;
                // English is prefetched on module load (iso3166.ts). If it
                // landed, unblock the UI — getCountriesCached() falls back to 'en'.
                if (isLanguageRegistered('en')) {
                    setIsLanguageLoaded(true);
                    return;
                }
                // Otherwise try one last English fetch as ultimate fallback.
                ensureGeoDataLoaded('en')
                    .then(() => { if (!cancelled) setIsLanguageLoaded(true); })
                    .catch(() => { /* already logged above */ });
            });

        return () => { cancelled = true; };
    }, [effectiveLanguage, autoImport]);

    const data = useMemo(() => {
        if (!isLanguageLoaded) {
            return {
                countries: {},
                getRegions: () => [] as ISORegion[],
                findRegion: () => null,
                mapGoogleToISO: () => null,
                isLanguageLoaded: false,
                registeredLanguages,
            };
        }

        try {
            const countriesArray = getCountries(effectiveLanguage);

            const countries: Record<string, ISOCountry> = {};
            countriesArray.forEach((country) => {
                countries[country.code] = {
                    iso: country.code,
                    iso3: country.iso3 || '',
                    numeric: country.numeric || 0,
                    name: country.name,
                };
            });

            const getRegions = (countryCode: string): ISORegion[] => {
                try {
                    const states = getStatesForCountry(countryCode, effectiveLanguage);
                    return states.map((state) => ({
                        iso: state.code,
                        name: state.name,
                    }));
                } catch {
                    return [];
                }
            };

            const findRegion = (countryCode: string, regionCode: string): ISORegion | null => {
                const regions = getRegions(countryCode);
                return regions.find((region) => region.iso === regionCode) ?? null;
            };

            const mapGoogleToISO = (
                googleState: string,
                googleStateLong: string,
                countryCode: string,
            ): ISORegion | null => {
                const regions = getRegions(countryCode);
                if (regions.length === 0) return null;

                let match = regions.find((r) => r.iso === googleState);
                if (match) return match;

                match = regions.find((r) => r.name.toLowerCase() === googleState.toLowerCase());
                if (match) return match;

                match = regions.find((r) => r.name.toLowerCase() === googleStateLong.toLowerCase());
                if (match) return match;

                match = regions.find(
                    (r) =>
                        r.name.toLowerCase().includes(googleStateLong.toLowerCase()) ||
                        googleStateLong.toLowerCase().includes(r.name.toLowerCase()),
                );

                return match ?? null;
            };

            return {
                countries,
                getRegions,
                findRegion,
                mapGoogleToISO,
                isLanguageLoaded,
                registeredLanguages,
            };
        } catch (error) {
            console.error('[SDK] Error loading ISO data:', error);
            return {
                countries: {},
                getRegions: () => [] as ISORegion[],
                findRegion: () => null,
                mapGoogleToISO: () => null,
                isLanguageLoaded,
                registeredLanguages,
            };
        }
    }, [effectiveLanguage, disputeSetting, isLanguageLoaded, registeredLanguages]);

    return data;
}



/**
 * Get available languages for ISO data
 */
export function getAvailableLanguages(): SupportedLanguage[] {
    // Return all available languages (not just registered ones)
    return ['en', 'ru', 'de', 'da', 'fr', 'es', 'zh', 'hi', 'pt', 'ja', 'ar', 'it', 'he'];
}

/**
 * Hook to manually import and register a language
 * @param language - Language code to import
 * @returns Object with loading state and error handling
 */
export function useLanguageImport(language: SupportedLanguage) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const importLanguageData = useCallback(async () => {
        if (isLanguageRegistered(language)) {
            return; // Already registered
        }

        setIsLoading(true);
        setError(null);

        try {
            await importLanguage(language);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to import language');
        } finally {
            setIsLoading(false);
        }
    }, [language]);

    return {
        importLanguage: importLanguageData,
        isLoading,
        error,
        isRegistered: isLanguageRegistered(language),
    };
}

/**
 * Get list of countries as options for select components
 * @param language - Language code (defaults to 'en')
 * @param autoImport - Whether to automatically import the language if not registered (default: true)
 */
export function useCountryOptions(
    language: SupportedLanguage = 'en',
    autoImport = true
): { value: string; label: string }[] {
    const { countries } = useISOData(language, autoImport);

    return useMemo(() => {
        return Object.entries(countries)
            .map(([code, country]) => ({
                value: code,
                label: country.name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [countries]);
}

/**
 * Get list of regions/states for a country as options for select components
 * @param countryCode - ISO country code
 * @param language - Language code (defaults to 'en')
 * @param autoImport - Whether to automatically import the language if not registered (default: true)
 */
export function useRegionOptions(
    countryCode: string,
    language: SupportedLanguage = 'en',
    autoImport = true
): { value: string; label: string }[] {
    const { getRegions } = useISOData(language, autoImport);

    return useMemo(() => {
        if (!countryCode) return [];

        const regions = getRegions(countryCode);
        return regions
            .map((region) => ({
                value: region.iso,
                label: region.name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [countryCode, getRegions]);
}
